import React from 'react';
import {Link} from 'react-router-dom';
import {useQuery} from 'react-query';
import SidePanel from '../ds/SidePanel.tsx';
import {Label} from '../ds/Badge.tsx';
import {ErrorState, LoadingState} from '../ds/StateView.tsx';
import useGetFileUsages from '../../api/uploads/useGetFileUsages.ts';
import {
    FileDataModel,
    FileProjectTabUsage,
    FileUsages,
    PublishStatus
} from '../../api/types.ts';

export const fileUsagesPanelId = 'asset-usages';

const tabParam = (index: number) => (index === 1 ? 'project' : `tab${index}`);

const plural = (count: number, one: string, many: string) =>
    `${count} ${count === 1 ? one : many}`;

// The places that aren't listed by name, as lines: "2 group posts".
function otherPlaces(usages: FileUsages): string[] {
    const lines: string[] = [];

    if (usages.otherProjects > 0) {
        lines.push(
            plural(usages.otherProjects, 'other project', 'other projects')
        );
    }
    if (usages.otherHelpArticles > 0) {
        lines.push(
            plural(usages.otherHelpArticles, 'Help article', 'Help articles')
        );
    }
    if (usages.inHelpTableOfContents) {
        lines.push('The Help table of contents');
    }
    if (usages.journalSections > 0) {
        lines.push(
            plural(usages.journalSections, 'journal entry', 'journal entries')
        );
    }
    if (usages.posts > 0) {
        lines.push(plural(usages.posts, 'group post', 'group posts'));
    }
    if (usages.groupIntros > 0) {
        lines.push(
            plural(usages.groupIntros, 'group question', 'group questions')
        );
    }
    if (usages.pictures > 0) {
        lines.push(
            plural(
                usages.pictures,
                'profile or group picture',
                'profile or group pictures'
            )
        );
    }

    return lines;
}

// "Tab 1: Project": the number first, since a first tab is so often titled "Project" that the title alone reads
// like a label.
const tabLabel = (tab: FileProjectTabUsage) =>
    tab.title?.trim()
        ? `Tab ${tab.index}: ${tab.title.trim()}`
        : `Tab ${tab.index}`;

const TabLink: React.FC<{projectId: string; tab: FileProjectTabUsage}> = ({
    projectId,
    tab
}) => (
    <li>
        <Link to={`/project/${projectId}?tab=${tabParam(tab.index)}`}>
            {tabLabel(tab)}
        </Link>
        {tab.hidden && <Label className="ms-2">Removed tab</Label>}
    </li>
);

const UsagesBody: React.FC<{file: FileDataModel}> = ({file}) => {
    const fetchUsages = useGetFileUsages(file.uri);
    const {
        data: usages,
        isLoading,
        isError,
        refetch
    } = useQuery(['fileUsages', file.uri], fetchUsages);

    if (isLoading) {
        return <LoadingState variant="rows" />;
    }

    if (isError || !usages) {
        return (
            <ErrorState title="Usage didn't load" onRetry={() => refetch()}>
                Check your connection and try again.
            </ErrorState>
        );
    }

    const others = otherPlaces(usages);

    return (
        <div className="file-usages">
            {!usages.indexComplete && (
                <p className="text-muted small">
                    Mixiply is still working out where files are used, so this
                    list may be short. Check again in a few minutes.
                </p>
            )}

            {usages.total === 0 && (
                <p>
                    Not used in any saved content. A project whose code puts
                    file names together as it runs could still use it.
                </p>
            )}

            {usages.projects.length > 0 && (
                <>
                    <h3 className="h6">Projects</h3>
                    <ul className="list-unstyled">
                        {usages.projects.map(project => (
                            <li key={project.id} className="mb-3">
                                <Link to={`/project/${project.id}`}>
                                    {project.name || 'Untitled project'}
                                </Link>
                                {project.ownerHandle && (
                                    <span className="text-muted small">
                                        {' · by '}
                                        <Link
                                            to={`/explore/groups/@${project.ownerHandle}`}
                                        >
                                            @{project.ownerHandle}
                                        </Link>
                                    </span>
                                )}
                                <ul className="list-unstyled ps-3 small">
                                    {project.tabs.map(tab => (
                                        <TabLink
                                            key={tab.index}
                                            projectId={project.id}
                                            tab={tab}
                                        />
                                    ))}
                                    {project.inProjectSettings && (
                                        <li>
                                            Project settings (thumbnail, website
                                            tab or template)
                                        </li>
                                    )}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {usages.helpArticles.length > 0 && (
                <>
                    <h3 className="h6">Help articles</h3>
                    <ul className="list-unstyled">
                        {usages.helpArticles.map(article => (
                            <li key={article.id}>
                                <Link to={`/content/${article.slug}`}>
                                    {article.title || article.slug}
                                </Link>
                                {article.status !== PublishStatus.Published && (
                                    <Label className="ms-2">
                                        {PublishStatus[article.status]}
                                    </Label>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {others.length > 0 && (
                <>
                    <h3 className="h6">
                        {usages.projects.length + usages.helpArticles.length > 0
                            ? 'Also in'
                            : 'Used in'}
                    </h3>
                    <ul className="list-unstyled">
                        {others.map(line => (
                            <li key={line}>{line}</li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};

/*
 * Where one file is used, from the asset reference index: the projects the
 * viewer can see (with a link to each tab), Help articles for content
 * editors, and counts of the rest.
 */
const FileUsagesPanel: React.FC<{file: FileDataModel | null}> = ({file}) => (
    <SidePanel
        id={fileUsagesPanelId}
        title={file ? `Where ${file.fileName} is used` : 'Where it is used'}
    >
        {file && <UsagesBody key={file.uri} file={file} />}
    </SidePanel>
);

export default FileUsagesPanel;
